import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveAgentId,
  selectActiveChatId,
  selectArtifactStreamingState,
  markArtifactContentProcessed,
} from './agentsChatsReducer';
import { useArtifacts } from '../common/artifacts/useArtifacts';
import { ArtifactData } from '../common/ai-chat/aiChatTypes';
import { useAgentChatInstance } from './AgentChatInstanceContext';

/**
 * Hook to handle artifact streaming for the active chat.
 *
 * This hook subscribes to the artifact streaming state in Redux and:
 * 1. Initiates streaming when artifact metadata arrives
 * 2. Incrementally adds content to the artifact panel as chunks arrive
 * 3. Completes streaming and renders the artifact when done
 * 4. Restores content from Redux when panel reopens during active streaming
 *
 * IMPORTANT: Should only be called ONCE per chat instance (in AgentChat component)
 * to prevent duplicate content processing.
 *
 * Uses `lastProcessedLength` in Redux as a safeguard against duplicates.
 */
export const useArtifactStreaming = () => {
  const dispatch = useDispatch();
  const { chatInstanceId } = useAgentChatInstance();
  const activeAgentId = useSelector(selectActiveAgentId);
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const artifactStreamingState = useSelector(selectArtifactStreamingState(activeChatId ?? ''));

  const {
    isArtifactPanelOpen,
    startArtifactStreaming,
    addStreamingContent,
    restoreStreamingContent,
    completeArtifactStreaming,
    addArtifactData,
  } = useArtifacts();

  const hasStartedStreaming = useRef(false);
  const wasPanelOpen = useRef(isArtifactPanelOpen);

  // Restore streaming content when panel reopens during active streaming
  // This handles the case where user closes the panel mid-stream and reopens it
  useEffect(() => {
    const panelJustOpened = isArtifactPanelOpen && !wasPanelOpen.current;
    wasPanelOpen.current = isArtifactPanelOpen;

    if (panelJustOpened && artifactStreamingState && !artifactStreamingState.isComplete) {
      const { accumulatedContent } = artifactStreamingState;
      if (accumulatedContent.length > 0) {
        // Restore all accumulated content from Redux to Context
        restoreStreamingContent(accumulatedContent);

        // Mark this content as processed so the main effect doesn't duplicate it
        if (activeAgentId && activeChatId) {
          dispatch(
            markArtifactContentProcessed({
              agentId: activeAgentId,
              chatId: activeChatId,
              processedLength: accumulatedContent.length,
            }),
          );
        }
      }
    }
  }, [
    isArtifactPanelOpen,
    artifactStreamingState,
    restoreStreamingContent,
    activeAgentId,
    activeChatId,
    dispatch,
  ]);

  // Track and handle artifact streaming - using Redux state as source of truth
  useEffect(() => {
    if (!artifactStreamingState) {
      hasStartedStreaming.current = false;
      return;
    }

    const { artifactId, versionId, title, accumulatedContent, lastProcessedLength, isComplete } =
      artifactStreamingState;
    const currentLength = accumulatedContent.length;

    // Artifact just started (metadata arrived)
    if (!hasStartedStreaming.current && artifactId) {
      startArtifactStreaming(artifactId);
      hasStartedStreaming.current = true;
    }

    // Artifact content is being streamed - only process new content
    if (currentLength > lastProcessedLength) {
      const newContent = accumulatedContent.substring(lastProcessedLength);
      addStreamingContent(newContent);

      // Mark this content as processed in Redux
      if (activeAgentId && activeChatId) {
        dispatch(
          markArtifactContentProcessed({
            agentId: activeAgentId,
            chatId: activeChatId,
            processedLength: currentLength,
          }),
        );
      }
    }

    // Artifact streaming completed
    if (isComplete && currentLength > 0) {
      // Infer content type from code markers
      const inferContentType = (code: string): 'react' | 'html' | 'javascript' => {
        // Remove the opening code fence to check the language tag
        const firstLine = code.split('\n')[0];
        if (firstLine.includes('jsx') || firstLine.includes('tsx')) {
          return 'react';
        }
        if (firstLine.includes('html')) {
          return 'html';
        }
        // Default to react for most artifacts
        return 'react';
      };

      // Clean up the artifact content (remove code fences)
      const cleanContent = accumulatedContent
        .replace(/^```[a-z]*\n/i, '') // Remove opening fence
        .replace(/\n```\s*$/i, ''); // Remove closing fence

      const contentType = inferContentType(accumulatedContent);

      // Add artifact data to the artifacts store
      const artifactData: ArtifactData = {
        artifactId,
        versions: [
          {
            versionId: versionId,
            title: title.replace(/^"|"$/g, ''), // Remove surrounding quotes if present
            content: cleanContent,
            contentType,
            createdAt: new Date().toISOString(),
          },
        ],
        currentVersion: versionId,
      };

      addArtifactData(artifactId, artifactData);
      completeArtifactStreaming();
    }
  }, [
    artifactStreamingState,
    activeAgentId,
    activeChatId,
    dispatch,
    startArtifactStreaming,
    addStreamingContent,
    completeArtifactStreaming,
    addArtifactData,
  ]);
};
